/**
 * Themes and the carcass contact-shading shader patch (cheap analytic AO inside the
 * openings). Port of design/shared/viewer.js.
 */
import * as THREE from 'three';

export type ThemeName = 'light' | 'dark';

export interface Theme {
  exposure: number;
  env: number;
  key: number;
  keyColour: number;
  fill: number;
  fillColour: number;
  shadow: number;
  contact: number;
  glow: number;
  glowColour: number;
  ink: string;
  inkSoft: string;
  halo: string;
  accent: string;
  figure: number;
  wall: number;
  floor: number;
}

export const THEMES: Record<ThemeName, Theme> = {
  light: {
    // Semblaro: a cool navy-tinted ground with a warm key on the wood (temperature contrast)
    exposure: 1.06, env: 0.5, key: 2.6, keyColour: 0xfff3e2, fill: 0.28, fillColour: 0xdbe6f8,
    shadow: 0.2, contact: 0.3, glow: 0, glowColour: 0xffe2b8,
    ink: '#041e45', inkSoft: 'rgba(4,30,69,.5)', halo: 'rgba(255,255,255,.92)',
    accent: '#0756bf', figure: 0xc9d1dc, wall: 0xeef2f6, floor: 0xdde3ea,
  },
  dark: {
    exposure: 1.02, env: 0.28, key: 3.2, keyColour: 0xffdcb0, fill: 0.14, fillColour: 0xa9bfff,
    shadow: 0.45, contact: 0.55, glow: 0.18, glowColour: 0xffe8c8,
    ink: '#e6edf7', inkSoft: 'rgba(230,237,247,.6)', halo: 'rgba(3,21,51,.88)',
    accent: '#7fb0ff', figure: 0x50627e, wall: 0x0c2550, floor: 0x061a3a,
  },
};

export const isThemeName = (name: unknown): name is ThemeName => name === 'light' || name === 'dark';

export const MAX_H = 12;

export interface AOUniforms {
  [name: string]: THREE.IUniform;
  /** W, H, D, t (m) */
  uCarcass: THREE.IUniform<THREE.Vector4>;
  uBack: THREE.IUniform<number>;
  uOrigin: THREE.IUniform<THREE.Vector3>;
  uHoriz: THREE.IUniform<THREE.Vector2[]>;
  uHCount: THREE.IUniform<number>;
  /** strength, radius (m), direct-light share */
  uAO: THREE.IUniform<THREE.Vector3>;
}

export function makeAOUniforms(): AOUniforms {
  return {
    uCarcass: { value: new THREE.Vector4(0.9, 1.8, 0.32, 0.018) },
    uBack: { value: 0.006 },
    uOrigin: { value: new THREE.Vector3() },
    uHoriz: { value: Array.from({ length: MAX_H }, () => new THREE.Vector2(-10, -10)) },
    uHCount: { value: 0 },
    uAO: { value: new THREE.Vector3(0.5, 0.055, 0.35) },
  };
}

export function patchAO(material: THREE.Material, U: AOUniforms): void {
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, U);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vCarcassPos;\nvarying vec3 vCarcassNormal;')
      .replace(
        '#include <project_vertex>',
        '#include <project_vertex>\nvCarcassPos = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvCarcassNormal = normalize(mat3(modelMatrix) * objectNormal);',
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
varying vec3 vCarcassPos;
varying vec3 vCarcassNormal;
uniform vec4 uCarcass;
uniform float uBack;
uniform vec3 uOrigin;
uniform vec2 uHoriz[${MAX_H}];
uniform int uHCount;
uniform vec3 uAO;
float carcassAO() {
  if (uAO.x <= 0.0) return 1.0;
  vec3 p = vCarcassPos - uOrigin;
  float W = uCarcass.x, H = uCarcass.y, D = uCarcass.z, t = uCarcass.w;
  float e = 0.0015;
  if (p.x < t - e || p.x > W - t + e || p.z < uBack - e || p.z > D + e || p.y < t - e || p.y > H - t + e) return 1.0;
  vec3 n = abs(normalize(vCarcassNormal));
  float r = uAO.y;
  float occ = 1.0;
  if (n.x < 0.7) { float dx = max(0.0, min(p.x - t, W - t - p.x)); occ *= 1.0 - 0.55 * exp(-dx / r); }
  if (n.z < 0.7) { float dz = max(0.0, p.z - uBack); occ *= 1.0 - 0.45 * exp(-dz / r); }
  if (n.y < 0.7) {
    float dy = 10.0;
    for (int i = 0; i < ${MAX_H}; i++) {
      if (i >= uHCount) break;
      vec2 h = uHoriz[i];
      if (p.y >= h.y) dy = min(dy, p.y - h.y); else if (p.y <= h.x) dy = min(dy, h.x - p.y);
    }
    occ *= 1.0 - 0.55 * exp(-dy / r);
  }
  float depth = clamp((D - p.z) / max(D - uBack, 0.01), 0.0, 1.0);
  occ *= 1.0 - 0.35 * depth * depth;
  return mix(1.0, occ, uAO.x);
}`,
      )
      .replace(
        '#include <aomap_fragment>',
        `#include <aomap_fragment>
{ float cao = carcassAO();
  reflectedLight.indirectDiffuse *= cao;
  reflectedLight.indirectSpecular *= mix(1.0, cao, 0.8);
  reflectedLight.directDiffuse *= mix(1.0, cao, uAO.z);
  reflectedLight.directSpecular *= mix(1.0, cao, uAO.z); }`,
      );
  };
  material.customProgramCacheKey = () => 'cutlist-carcass-ao';
}

export const BOOK_COLOURS = ['#7f8a76', '#d9d2c4', '#c8bba5', '#5f6b62', '#e6e0d4', '#a4775c', '#3f4447', '#b9ab92', '#8d9690', '#cfc6b6'];

/** Stylised 1750 mm mannequin. Returns the group and its geometries (for disposal). */
export function buildFigure(material: THREE.Material): { group: THREE.Group; geometries: THREE.BufferGeometry[] } {
  const g = new THREE.Group();
  g.name = 'scale-figure';
  const geos: THREE.BufferGeometry[] = [];
  const add = (geo: THREE.BufferGeometry, x: number, y: number, z: number, sx = 1, sy = 1, sz = 1, rz = 0) => {
    geos.push(geo);
    const m = new THREE.Mesh(geo, material);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    m.rotation.z = rz;
    m.castShadow = true;
    m.receiveShadow = true;
    g.add(m);
  };
  const leg = new THREE.CapsuleGeometry(0.062, 0.72, 6, 14);
  add(leg, -0.088, 0.422, 0, 1, 1, 0.95);
  add(leg, 0.088, 0.422, 0, 1, 1, 0.95);
  add(new THREE.CapsuleGeometry(0.15, 0.4, 8, 18), 0, 1.165, 0, 0.92, 1, 0.58);
  add(new THREE.CylinderGeometry(0.045, 0.05, 0.1, 12), 0, 1.545, 0);
  add(new THREE.SphereGeometry(0.105, 20, 14), 0, 1.6366, 0.004, 0.9, 1.08, 0.97);
  const arm = new THREE.CapsuleGeometry(0.042, 0.6, 6, 12);
  add(arm, -0.212, 1.13, 0, 1, 1, 1, -0.06);
  add(arm, 0.212, 1.13, 0, 1, 1, 1, 0.06);
  return { group: g, geometries: [...new Set(geos)] };
}
